import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Chip,
  Stack,
  Divider,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { Document, DocumentType, User } from '../../types';
import { formatDistanceToNow } from 'date-fns';

interface DocumentViewerProps {
  document: Document;
  currentUser: User;
  open: boolean;
  onClose: () => void;
  onUpdateDocument: (documentId: string, updates: any) => Promise<Document>;
  onDeleteDocument: (documentId: string) => void;
  onApproveDocument: (documentId: string) => void;
  onRejectDocument: (documentId: string, reason: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  currentUser,
  open,
  onClose,
  onUpdateDocument,
  onDeleteDocument,
  onApproveDocument,
  onRejectDocument,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [editTitle, setEditTitle] = useState(document.title);
  const [editType, setEditType] = useState(document.type);
  const [editDescription, setEditDescription] = useState(document.description || '');
  const [editDate, setEditDate] = useState(document.date || '');
  const [editLocation, setEditLocation] = useState(document.location || '');
  const [editTranscription, setEditTranscription] = useState(document.transcription || '');

  // Reset editable fields when document changes
  React.useEffect(() => {
    setEditTitle(document.title);
    setEditType(document.type);
    setEditDescription(document.description || '');
    setEditDate(document.date || '');
    setEditLocation(document.location || '');
    setEditTranscription(document.transcription || '');
    setIsEditing(false);
    setError(null);
  }, [document]);

  // Check permissions
  const canEdit = currentUser.role === 'admin' || document.createdBy === currentUser.uid;
  const canDelete = currentUser.role === 'admin' || document.createdBy === currentUser.uid;
  const canModerate = currentUser.role === 'admin';

  // Format document type for display
  const formatDocumentType = (type: DocumentType): string => {
    return type
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get approval status color
  const getApprovalStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  // Handle save
  const handleSave = useCallback(async () => {
    if (!editTitle.trim()) {
      setError('Title is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updates: any = {
        title: editTitle.trim(),
        type: editType,
        description: editDescription.trim(),
        date: editDate || undefined,
        location: editLocation.trim() || undefined,
      };

      // Only include transcription if it's different
      if (editTranscription !== document.transcription) {
        updates.transcription = editTranscription.trim();
      }

      await onUpdateDocument(document.id!, updates);
      setIsEditing(false);
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  }, [editTitle, editType, editDescription, editDate, editLocation, editTranscription, document, onUpdateDocument]);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditTitle(document.title);
    setEditType(document.type);
    setEditDescription(document.description || '');
    setEditDate(document.date || '');
    setEditLocation(document.location || '');
    setEditTranscription(document.transcription || '');
    setIsEditing(false);
    setError(null);
  }, [document]);

  // Handle delete
  const handleDelete = useCallback(() => {
    onDeleteDocument(document.id!);
    onClose();
  }, [document, onDeleteDocument, onClose]);

  // Handle approve
  const handleApprove = useCallback(() => {
    onApproveDocument(document.id!);
  }, [document, onApproveDocument]);

  // Handle reject
  const handleReject = useCallback(() => {
    const reason = window.prompt('Reason for rejection (optional):');
    if (reason !== null) {
      onRejectDocument(document.id!, reason);
    }
  }, [document, onRejectDocument]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">
            Document Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={document.approvalStatus}
              size="small"
              color={getApprovalStatusColor(document.approvalStatus)}
            />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Error message */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
          <Tab label="Details" />
          <Tab label="Transcription" />
          <Tab label="History" />
        </Tabs>

        {/* Details Tab */}
        <TabPanel value={activeTab} index={0}>
          <Stack spacing={3}>
            {/* Document preview/link */}
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="subtitle2" gutterBottom>
                Document File
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {document.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {(document.fileSize / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenIcon />}
                href={document.storageURL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ mt: 1 }}
              >
                Open Document
              </Button>
            </Paper>

            {/* Metadata */}
            <TextField
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
              required
              disabled={!isEditing || isSaving}
              InputProps={{
                readOnly: !isEditing,
              }}
            />

            <FormControl fullWidth required disabled={!isEditing || isSaving}>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={editType}
                label="Document Type"
                onChange={(e) => setEditType(e.target.value as DocumentType)}
                readOnly={!isEditing}
              >
                <MenuItem value="birth-certificate">Birth Certificate</MenuItem>
                <MenuItem value="death-certificate">Death Certificate</MenuItem>
                <MenuItem value="marriage-certificate">Marriage Certificate</MenuItem>
                <MenuItem value="divorce-decree">Divorce Decree</MenuItem>
                <MenuItem value="census-record">Census Record</MenuItem>
                <MenuItem value="military-record">Military Record</MenuItem>
                <MenuItem value="will">Will</MenuItem>
                <MenuItem value="deed">Deed</MenuItem>
                <MenuItem value="letter">Letter</MenuItem>
                <MenuItem value="diary">Diary</MenuItem>
                <MenuItem value="obituary">Obituary</MenuItem>
                <MenuItem value="newspaper-clipping">Newspaper Clipping</MenuItem>
                <MenuItem value="immigration-record">Immigration Record</MenuItem>
                <MenuItem value="naturalization-papers">Naturalization Papers</MenuItem>
                <MenuItem value="school-record">School Record</MenuItem>
                <MenuItem value="employment-record">Employment Record</MenuItem>
                <MenuItem value="medical-record">Medical Record</MenuItem>
                <MenuItem value="tax-record">Tax Record</MenuItem>
                <MenuItem value="bible-record">Bible Record</MenuItem>
                <MenuItem value="church-record">Church Record</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              disabled={!isEditing || isSaving}
              InputProps={{
                readOnly: !isEditing,
              }}
            />

            <TextField
              label="Date"
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              fullWidth
              disabled={!isEditing || isSaving}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                readOnly: !isEditing,
              }}
            />

            <TextField
              label="Location"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
              fullWidth
              disabled={!isEditing || isSaving}
              InputProps={{
                readOnly: !isEditing,
              }}
            />
          </Stack>
        </TabPanel>

        {/* Transcription Tab */}
        <TabPanel value={activeTab} index={1}>
          <TextField
            label="Transcription"
            value={editTranscription}
            onChange={(e) => setEditTranscription(e.target.value)}
            fullWidth
            multiline
            rows={12}
            disabled={!isEditing || isSaving}
            InputProps={{
              readOnly: !isEditing,
            }}
            helperText="Type or paste the text content of this document"
            placeholder="No transcription available. Click Edit to add one."
          />
        </TabPanel>

        {/* History Tab */}
        <TabPanel value={activeTab} index={2}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Uploaded By
              </Typography>
              <Typography variant="body1">{document.uploadedByName || 'Unknown'}</Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(document.createdAt))} ago
              </Typography>
            </Box>

            {document.updatedAt && document.updatedAt !== document.createdAt && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">{document.updatedByName || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDistanceToNow(new Date(document.updatedAt))} ago
                </Typography>
              </Box>
            )}

            {document.approvalStatus === 'approved' && document.approvedBy && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Approved By
                </Typography>
                <Typography variant="body1">{document.approvedBy || 'Unknown'}</Typography>
                {document.approvedAt && (
                  <Typography variant="caption" color="text.secondary">
                    {formatDistanceToNow(new Date(document.approvedAt))} ago
                  </Typography>
                )}
              </Box>
            )}

            {document.approvalStatus === 'rejected' && document.rejectionReason && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Rejection Reason
                </Typography>
                <Alert severity="warning">{document.rejectionReason}</Alert>
              </Box>
            )}
          </Stack>
        </TabPanel>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Box>
          {/* Moderation actions (Admin only) */}
          {canModerate && document.approvalStatus === 'pending' && !isEditing && (
            <>
              <Button startIcon={<ApproveIcon />} color="success" onClick={handleApprove} sx={{ mr: 1 }}>
                Approve
              </Button>
              <Button startIcon={<RejectIcon />} color="error" onClick={handleReject}>
                Reject
              </Button>
            </>
          )}

          {/* Delete action */}
          {canDelete && !isEditing && (
            <Button startIcon={<DeleteIcon />} color="error" onClick={handleDelete}>
              Delete
            </Button>
          )}
        </Box>

        <Box>
          {/* Edit/Save/Cancel actions */}
          {canEdit && !isEditing && (
            <Button startIcon={<EditIcon />} onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}

          {isEditing && (
            <>
              <Button onClick={handleCancelEdit} disabled={isSaving} sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !editTitle.trim()}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}

          {!isEditing && (
            <Button onClick={onClose} sx={{ ml: 1 }}>
              Close
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
